import { IonIcon, useIonPicker } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { timeOutline } from 'ionicons/icons'
import type React from 'react'
import { useMemo } from 'react'
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form'
import { fieldStyles } from './fieldStyles'

/** Date + time wheel-picker field over react-hook-form (v1 form/Time). */

const timeStyles = {
  timeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  } as React.CSSProperties,
  timeValue: {
    fontSize: '16px',
    fontWeight: 500,
    color: 'var(--cs-color-text, #1a1a1a)',
  } as React.CSSProperties,
  timePlaceholder: {
    fontSize: '15px',
    color: 'var(--cs-color-text-secondary, #999)',
  } as React.CSSProperties,
}

const generateHours = () => {
  const arr = Array.from({ length: 11 }, (_, i) => i + 1)
  arr.unshift(12)
  return arr
}
const generateMinutes = (step = 5) =>
  Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step)

/** Local yyyy-mm-dd key for a date (used as the picker's date-column value). */
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`

/** Days from `before` days ago to `after` days ahead (anchored on today). */
const generateDates = (today: Date, before = 7, after = 7) => {
  const base = new Date(today)
  base.setHours(0, 0, 0, 0)
  return Array.from({ length: before + after + 1 }, (_, i) => {
    const d = new Date(base)
    d.setDate(base.getDate() + (i - before))
    return d
  })
}

/** Wheel position (12h clock) for a Date, with an explicit minute value. */
const wheelTimeFromDate = (d: Date, minute: number) => {
  const h = d.getHours()
  return {
    hour: h === 0 ? 12 : h > 12 ? h - 12 : h,
    minute,
    meridian: h >= 12 ? 'PM' : 'AM',
  }
}

/**
 * Initial wheel position: the entered/planned time when valid, else the
 * current time rounded to the 5-minute step.
 */
const initialWheelTime = (source: string | undefined) => {
  if (source) {
    const d = new Date(source)
    if (!Number.isNaN(d.getTime())) return wheelTimeFromDate(d, d.getMinutes())
    return { hour: 12, minute: 0, meridian: 'AM' }
  }
  const now = new Date()
  return wheelTimeFromDate(now, Math.round(now.getMinutes() / 5) * 5)
}

export type TimeProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> & {
    label: string
    placeholder: string
    disabled?: boolean
    /** Planned time — the picker wheel opens here when the field is still empty,
     *  so the operator actively confirms/adjusts it (the field itself stays
     *  blank/placeholder until they pick). */
    plannedTime?: string
  }

export const Time = <TFieldValues extends FieldValues>({
  label,
  placeholder,
  disabled,
  plannedTime,
  ...controllerProps
}: TimeProps<TFieldValues>) => {
  const { _ } = useLingui()
  const {
    field: { value: rawValue, onChange },
  } = useController(controllerProps)
  const value = rawValue as string | undefined

  const [presentPicker] = useIonPicker()

  /** Column label for a day: "Today" or "Mon Jun 15" (matches v1's TimePicker). */
  const dayLabel = (d: Date, today: Date) =>
    ymd(d) === ymd(today)
      ? _(msg`Today`)
      : d
          .toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          })
          .replace(',', '')

  const displayValue = useMemo(() => {
    if (!value) return ''
    const d = new Date(value)
    // Unparseable value: show it raw rather than hiding the problem
    if (Number.isNaN(d.getTime())) return value
    const time = d.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    if (ymd(d) === ymd(new Date())) return time
    const date = d
      .toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
      .replace(',', '')
    return `${date}, ${time}`
  }, [value])

  const openPicker = () => {
    if (disabled) return

    // Pre-select the wheel from the entered value, else the PLANNED time (so the
    // operator confirms/adjusts it rather than assuming the system filled it),
    // else the current time.
    const source = value || plannedTime
    const {
      hour: initHour,
      minute: initMinute,
      meridian: initMeridian,
    } = initialWheelTime(source)

    const today = new Date()
    const dates = generateDates(today)
    const sourceDate = source ? new Date(source) : today
    const anchorDate =
      source && !Number.isNaN(sourceDate.getTime()) ? sourceDate : today
    const dateIdx = dates.findIndex((d) => ymd(d) === ymd(anchorDate))

    const hours = generateHours()
    const minutes = generateMinutes(5)

    const hourIdx = hours.indexOf(initHour)
    const minuteIdx = minutes.reduce(
      (best, m, i) =>
        Math.abs(m - initMinute) < Math.abs(minutes[best] - initMinute)
          ? i
          : best,
      0,
    )

    presentPicker({
      mode: 'ios',
      columns: [
        {
          name: 'date',
          columnWidth: '140px',
          selectedIndex: dateIdx >= 0 ? dateIdx : 0,
          options: dates.map((d) => ({
            text: dayLabel(d, today),
            value: ymd(d),
          })),
        },
        {
          name: 'hour',
          columnWidth: '40px',
          selectedIndex: hourIdx >= 0 ? hourIdx : 0,
          options: hours.map((h) => ({
            text: h.toString(),
            value: h === 12 ? 0 : h,
          })),
        },
        {
          name: 'minute',
          columnWidth: '40px',
          selectedIndex: minuteIdx,
          options: minutes.map((m) => ({
            text: m.toString().padStart(2, '0'),
            value: m,
          })),
        },
        {
          name: 'meridian',
          columnWidth: '50px',
          selectedIndex: initMeridian === 'PM' ? 1 : 0,
          options: [
            { text: 'AM', value: 'AM' },
            { text: 'PM', value: 'PM' },
          ],
        },
      ],
      buttons: [
        { text: _(msg`Cancel`), role: 'cancel' },
        {
          text: _(msg`Done`),
          handler: (selected) => {
            const hour24 =
              selected.meridian.value === 'PM'
                ? selected.hour.value + 12
                : selected.hour.value
            const [year, month, day] = String(selected.date.value)
              .split('-')
              .map(Number)
            const picked = new Date(
              year,
              month - 1,
              day,
              hour24,
              selected.minute.value,
              0,
              0,
            )
            onChange(picked.toISOString())
          },
        },
      ],
    })
  }

  return (
    <div style={fieldStyles.item}>
      <span style={fieldStyles.label}>{label}</span>
      <button
        type='button'
        data-field={controllerProps.name}
        style={{
          ...fieldStyles.buttonReset,
          ...timeStyles.timeRow,
          ...(disabled ? { opacity: 0.45 } : {}),
        }}
        onClick={openPicker}
      >
        <span
          style={
            displayValue ? timeStyles.timeValue : timeStyles.timePlaceholder
          }
        >
          {displayValue || placeholder}
        </span>
        <IonIcon
          icon={timeOutline}
          style={{ fontSize: '20px', color: 'var(--ion-color-medium)' }}
        />
      </button>
    </div>
  )
}
